<script setup lang="ts">
import { MeasValueType, TrackItDataSequenceInfoDto } from '@/api/generator';
import Button from '@/components/buttons/Button.vue';
import KeyValueTable from '@/components/KeyValueTable/KeyValueTable.vue';
import { downloadDataSequenceFile } from '@/utils/download';
import { I18nKey } from '@/utils/typings';
import { mdiChartBellCurve, mdiDownload } from '@mdi/js';
import { computed, ref } from 'vue';
import TrackItProfileChartDialog from './TrackItProfileChartDialog.vue';

const props = defineProps<{
  title: string;
  measurementData: TrackItDataSequenceInfoDto[];
}>();

const chartOpen = ref(false);
const selectedCurve = ref<TrackItDataSequenceInfoDto | null>(null);

function openChart(item: TrackItDataSequenceInfoDto) {
  selectedCurve.value = item;
  chartOpen.value = true;
}

// Plottability is decided by the presence of a positions child, not by measurementType. An
// unrecognised type attribute in the XML falls back to UserDefined, so a real curve can arrive
// typed UserDefined while still carrying its positions — which is why a chart takes precedence
// over the download affordance.
function isCurve(item: TrackItDataSequenceInfoDto) {
  return item.positions != null;
}

function isFile(item: TrackItDataSequenceInfoDto) {
  return (
    !isCurve(item) &&
    (item.measurementType === MeasValueType.UserDefined ||
      item.measurementType === MeasValueType.Mcc ||
      item.measurementType === MeasValueType.Xcc)
  );
}

const downloadingId = ref<number | null>(null);

async function download(item: TrackItDataSequenceInfoDto) {
  if (item.id == null) return;

  downloadingId.value = item.id;
  try {
    await downloadDataSequenceFile(item.id, item.name);
  } finally {
    downloadingId.value = null;
  }
}

// Already a display-ready string from the backend — the requirement asks for no formatting.
function scalarValue(item: TrackItDataSequenceInfoDto) {
  const unit = item.unit ?? '';
  return item.scalarValue == null ? '' : `${item.scalarValue} ${unit}`.trim();
}

const rows = computed(() =>
  props.measurementData.map((item) => ({ label: item.name ?? '', item })),
);
</script>

<template>
  <div>
    <h5>{{ title }}</h5>

    <p v-if="!measurementData.length">{{ $t('TrackItNoMeasurementData' as I18nKey) }}</p>

    <KeyValueTable v-else :rows="rows">
      <template #value="{ row: { item } }">
        <Button
          v-if="isCurve(item)"
          variant="secondary"
          size="small"
          :icon="mdiChartBellCurve"
          iconPosition="start"
          :label="$t('TrackItShowGraphics' as I18nKey)"
          @click="openChart(item)"
        />

        <Button
          v-else-if="isFile(item)"
          variant="secondary"
          size="small"
          :icon="mdiDownload"
          iconPosition="start"
          :loading="downloadingId === item.id"
          :label="$t('TrackItDownloadFile' as I18nKey)"
          @click="download(item)"
        />

        <span v-else>{{ scalarValue(item) }}</span>
      </template>
    </KeyValueTable>

    <TrackItProfileChartDialog
      v-if="selectedCurve"
      :key="selectedCurve.id"
      v-model="chartOpen"
      :curve="selectedCurve"
    />
  </div>
</template>
